import { ChangeDetectionStrategy, Component, computed, effect, input, OnDestroy, output, signal, ViewEncapsulation } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { CeButtonComponent } from '../ce-button/ce-button.component';
import { CeIconComponent } from '../ce-icon/ce-icon.component';
import { DsProgressBarComponent } from '../ds-progress-bar/ds-progress-bar.component';
import { ICONLIB, Icons } from '../ce-icon/ICONLIB';
import { Observable, Subscription } from 'rxjs';

// Derived from ICONLIB: any icon named "file-{ext}" is automatically mapped to that extension
const FILE_ICON_MAP: Record<string, keyof Icons> = Object.fromEntries(
    (Object.keys(ICONLIB) as (keyof Icons)[])
        .filter((k): k is `file-${string}` & keyof Icons => k.startsWith('file-'))
        .map((k) => [k.slice('file-'.length), k])
);

const SUCCESS_DISPLAY_MS = 2000;
const BYTES_PER_KB = 1024;
const BYTES_PER_MB = 1024 * 1024;

@Component({
    selector: 'ds-file-upload',
    imports: [CeButtonComponent, CeIconComponent, DsProgressBarComponent, NgTemplateOutlet],
    templateUrl: './ds-file-upload.component.html',
    styleUrl: './ds-file-upload.component.scss',
    host: {
        class: 'DsFileUpload',
    },
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DsFileUploadComponent implements OnDestroy {
    acceptedTypes = input.required<string>();
    /** Called after file selection. Return an Observable emitting progress values (0–100). */
    uploadFn = input.required<(files: File[]) => Observable<number>>();
    multiple = input<boolean>(false);
    maxSizeMb = input<number | null>(null);

    fileSelected = output<File[]>();
    fileRemoved = output<File>();

    readonly dragActive = signal(false);
    readonly currentFiles = signal<File[]>([]);
    readonly errorFile = signal<File | null>(null);
    readonly typeError = signal('');
    readonly #uploadError = signal<string | null>(null);

    readonly hasFile = computed(() => this.currentFiles().length > 0);
    readonly activeError = computed(() => {
        if (this.typeError() && this.errorFile()) return { file: this.errorFile()!, message: this.typeError() };
        if (this.#uploadError() && this.currentFiles().length) return { file: this.currentFiles().at(-1)!, message: this.#uploadError()! };
        return null;
    });
    readonly #parsedTypes = computed(() => this.acceptedTypes().split(',').map((t) => t.trim().toLowerCase()));
    readonly fileTypeLabel = computed(() => {
        const types = this.#parsedTypes();
        return types.length > 1 ? `${types.slice(0, -1).join(', ')} or ${types.at(-1)}` : types[0];
    });
    protected readonly resolvedProgress = signal<number | null>(null);
    readonly showProgress = computed(() => this.resolvedProgress() !== null);
    readonly progressComplete = signal(false);
    readonly showSuccess = signal<ReadonlySet<File>>(new Set());
    readonly uploadingFiles = signal<ReadonlySet<File>>(new Set());

    #successTimer: ReturnType<typeof setTimeout> | null = null;
    #progressCompleteTimer: ReturnType<typeof setTimeout> | null = null;
    #uploadSub: Subscription | null = null;

    constructor() {
        effect(() => {
            if (this.resolvedProgress() === 100) {
                if (this.#progressCompleteTimer !== null) clearTimeout(this.#progressCompleteTimer);
                this.#progressCompleteTimer = setTimeout(() => this.progressComplete.set(true), 400);
            } else {
                if (this.#progressCompleteTimer !== null) {
                    clearTimeout(this.#progressCompleteTimer);
                    this.#progressCompleteTimer = null;
                }
                this.progressComplete.set(false);
            }
        });
    }

    ngOnDestroy(): void {
        this.#uploadSub?.unsubscribe();
    }

    onProgressAnimationDone(): void {
        this.resolvedProgress.set(null);
        this.showSuccess.set(new Set(this.uploadingFiles()));
        this.uploadingFiles.set(new Set());
        if (this.#successTimer !== null) clearTimeout(this.#successTimer);
        this.#successTimer = setTimeout(() => this.showSuccess.set(new Set()), SUCCESS_DISPLAY_MS);
    }

    fileIconOf(file: File): keyof Icons {
        const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
        return FILE_ICON_MAP[ext] ?? 'note-add';
    }

    fileSizeOf(file: File): string {
        const bytes = file.size;
        return bytes < BYTES_PER_MB
            ? `${Math.round(bytes / BYTES_PER_KB)} KB`
            : `${(bytes / BYTES_PER_MB).toFixed(1)} MB`;
    }

    onDragOver(event: DragEvent): void {
        event.preventDefault();
        this.dragActive.set(true);
    }

    onDragLeave(event: DragEvent): void {
        event.preventDefault();
        this.dragActive.set(false);
    }

    onDrop(event: DragEvent): void {
        event.preventDefault();
        this.dragActive.set(false);
        const files = Array.from(event.dataTransfer?.files ?? []);
        this.#handleFiles(files);
    }

    protected errorFileName(file: File): string {
        return file.type !== '' ? file.name : 'Unknown file type';
    }

    onFileChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        const files = Array.from(input.files ?? []);
        this.#handleFiles(files);
        input.value = '';
    }

    clearError(): void {
        const last = this.currentFiles().at(-1);
        if (this.#uploadError() && last) {
            this.removeFile(last);
        }
        this.typeError.set('');
        this.errorFile.set(null);
        this.#uploadError.set(null);
    }

    removeFile(file: File): void {
        this.currentFiles.update((files) => files.filter((f) => f !== file));
        this.fileRemoved.emit(file);
    }

    #validateFiles(incoming: File[]): { valid: File[]; firstInvalid: File | null; firstError: string } {
        const parsedTypes = this.#parsedTypes();
        const maxSizeMb = this.maxSizeMb();
        const valid: File[] = [];
        let firstInvalid: File | null = null;
        let firstError = '';

        for (const file of incoming) {
            const wrongType = !parsedTypes.some((ext) => file.name.toLowerCase().endsWith(ext));
            const tooBig = maxSizeMb !== null && file.size > maxSizeMb * BYTES_PER_MB;

            if (wrongType && !firstInvalid) {
                firstInvalid = file;
                firstError = `File type not supported. Use ${this.fileTypeLabel()} instead.`;
            } else if (tooBig && !firstInvalid) {
                firstInvalid = file;
                firstError = `File exceeds the ${maxSizeMb} MB size limit.`;
            } else if (!wrongType && !tooBig) {
                valid.push(file);
            }
        }

        return { valid, firstInvalid, firstError };
    }

    #handleFiles(incoming: File[]): void {
        if (this.#uploadError() && this.currentFiles().length) {
            const errored = this.currentFiles().at(-1)!;
            this.currentFiles.update((files) => files.filter((f) => f !== errored));
            this.fileRemoved.emit(errored);
            this.#uploadError.set(null);
        }

        const { valid, firstInvalid, firstError } = this.#validateFiles(incoming);

        if (firstInvalid) {
            this.typeError.set(firstError);
            this.errorFile.set(firstInvalid);
        } else {
            this.typeError.set('');
            this.errorFile.set(null);
        }

        if (valid.length === 0) return;

        let newFiles: File[];
        if (this.multiple()) {
            const existing = this.currentFiles();
            const names = new Set(existing.map((f) => f.name));
            newFiles = valid.filter((f) => !names.has(f.name));
            this.currentFiles.set([...existing, ...newFiles]);
        } else {
            newFiles = [valid[0]];
            this.currentFiles.set(newFiles);
        }

        this.fileSelected.emit(this.currentFiles());

        this.#uploadSub?.unsubscribe();
        this.uploadingFiles.set(new Set(newFiles));
        this.resolvedProgress.set(0);
        this.#uploadSub = this.uploadFn()(this.currentFiles()).subscribe({
            next: (pct) => this.resolvedProgress.set(pct),
            error: (err: unknown) => {
                this.resolvedProgress.set(null);
                this.uploadingFiles.set(new Set());
                this.#uploadError.set(err instanceof Error ? err.message : 'Upload failed. Please try again.');
            },
            complete: () => this.resolvedProgress.set(100),
        });
    }
}
